import { useAppStore } from "@hooks/useAppStore";
import useCache from "@hooks/useCache";
import useCacheKey from "@hooks/useCacheKey";
import useClerkUser from "@hooks/useClerkUser";
import { type ConsentData, useGlobalStore } from "@hooks/useGlobalStore";
import { useLocation } from "@hooks/useLocation";
import { useToast } from "@memorang/ui/src/components/Toast";
import { DateTime } from "luxon";
import { useCallback, useMemo } from "react";

const INDIA_COUNTRY_CODE = "IN";

const defaultConsentData: ConsentData = {
	privacy_notice: undefined,
	mobile_app_terms: undefined,
	ai_tool_terms: undefined,
	marketing_emails: undefined,
};

export const useConsent = () => {
	const { countryCode } = useLocation();
	const { consentCacheKey } = useCacheKey();
	const { isSignedIn } = useClerkUser();
	const { showToast } = useToast();
	const aiToolsTosUrl = useAppStore((store) => store.app.links.aiToolsTos);
	const hasAiToolsTos = !!aiToolsTosUrl;
	const pendingConsent = useGlobalStore((state) => state.pendingConsent);
	const setPendingConsent = useGlobalStore((state) => state.setPendingConsent);

	const { data, isLoading, setCache, isUpdating } = useCache<ConsentData>({
		key: consentCacheKey || "",
		defaultData: defaultConsentData,
		enabled: !!consentCacheKey && isSignedIn,
		staleTime: 0,
		shouldInvalidate: true,
		shouldOptimisticUpdate: true,
	});

	const isIndia = countryCode === INDIA_COUNTRY_CODE;
	const requiresExplicitConsent = isIndia;

	const consentData = useMemo(() => {
		if (isSignedIn && consentCacheKey) {
			return data || defaultConsentData;
		}
		return pendingConsent || defaultConsentData;
	}, [isSignedIn, consentCacheKey, data, pendingConsent]);

	const isConsentChecked = useMemo(() => {
		if (!consentData) {
			return false;
		}
		const hasPrivacyConsent = !!consentData.privacy_notice?.agreed;
		const hasMobileAppConsent = !!consentData.mobile_app_terms?.agreed;
		const hasAiToolsConsent =
			!hasAiToolsTos || !!consentData.ai_tool_terms?.agreed;

		return hasPrivacyConsent && hasMobileAppConsent && hasAiToolsConsent;
	}, [consentData, hasAiToolsTos]);

	const createConsentData = useCallback((): ConsentData => {
		const now = DateTime.utc().toISO();
		const consentData: ConsentData = {
			privacy_notice: {
				agreed: true,
				agreed_at: now || "",
			},
			mobile_app_terms: {
				agreed: true,
				agreed_at: now || "",
			},
		};

		if (hasAiToolsTos) {
			consentData.ai_tool_terms = {
				agreed: true,
				agreed_at: now || "",
			};
		}

		return consentData;
	}, [hasAiToolsTos]);

	const handleConsentChange = useCallback(
		async (checked: boolean) => {
			const consentDataToSave = checked
				? createConsentData()
				: defaultConsentData;

			if (isSignedIn && consentCacheKey) {
				try {
					await setCache(consentDataToSave);
				} catch {
					showToast({
						message: "Failed to save consent. Please try again.",
					});
				}
			} else {
				setPendingConsent(consentDataToSave);
			}
		},
		[
			isSignedIn,
			consentCacheKey,
			setCache,
			showToast,
			createConsentData,
			setPendingConsent,
		],
	);

	const saveConsent = useCallback(
		async (consentData: ConsentData) => {
			if (isSignedIn && consentCacheKey) {
				try {
					await setCache(consentData);
				} catch {
					showToast({
						message: "Failed to save consent. Please try again.",
					});
					throw new Error("Failed to save consent");
				}
			} else {
				setPendingConsent(consentData);
			}
		},
		[isSignedIn, consentCacheKey, setCache, showToast, setPendingConsent],
	);

	const handleContinue = useCallback(
		async (onSuccess: () => void) => {
			if (requiresExplicitConsent && !isConsentChecked) {
				return;
			}

			try {
				const consentData = createConsentData();
				await saveConsent(consentData);
				onSuccess();
			} catch {
				showToast({
					message: "Failed to save consent. Please try again.",
				});
			}
		},
		[
			requiresExplicitConsent,
			isConsentChecked,
			createConsentData,
			saveConsent,
			showToast,
		],
	);

	return {
		isIndia,
		requiresExplicitConsent,
		isConsentChecked,
		isSaving: isLoading || isUpdating,
		handleConsentChange,
		handleContinue,
		saveConsent,
		createConsentData,
		consentData,
	};
};
